import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { useAuth } from '@/modules/auth/hooks/useAuth'
import { RoutesApi, WaypointResponse, CreateWaypointDto, UpdateWaypointDto } from '@/shared/api/routes'
import { TripsApi } from '@/shared/api/trips'

interface UseTripWaypointsOptions {
  tripId?: string
}

/**
 * Hook to manage waypoints at trip level
 * Waypoints are organized via trip_waypoints junction table (routes are deprecated)
 */
export function useTripWaypoints({ tripId }: UseTripWaypointsOptions) {
  const { getToken } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [waypoints, setWaypoints] = useState<WaypointResponse[]>([])
  
  // Track previous tripId and abort controller to prevent duplicate fetches
  const prevTripIdRef = useRef<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const isFetchingRef = useRef<boolean>(false)

  // Memoize API instances to prevent unnecessary recreations
  const routesApi = useMemo(() => new RoutesApi(getToken), [getToken])
  const tripsApi = useMemo(() => new TripsApi(getToken), [getToken])

  /**
   * Fetch trip waypoints for a trip
   * Uses GET /itinerary/waypoints?trip_id=X
   * @param options - Optional cache control (use 'no-store' to bypass cache)
   */
  const fetchWaypoints = useCallback(async (options?: { cache?: RequestCache }) => {
    if (!tripId) {
      setWaypoints([])
      prevTripIdRef.current = null
      return
    }

    // Skip if already fetching for this tripId
    // Note: Don't return early here - we want to ensure loading state is set correctly
    // The actual fetch will be prevented by the abort controller check below
    if (isFetchingRef.current && prevTripIdRef.current === tripId) {
      // If already fetching, just ensure loading state is true and return
      // The existing fetch will handle setting loading to false when done
      if (!loading) {
        setLoading(true)
      }
      return
    }

    // Cancel previous request if tripId changed
    if (abortControllerRef.current && prevTripIdRef.current !== tripId && prevTripIdRef.current !== null) {
      abortControllerRef.current.abort()
    }

    // Create new abort controller for this request
    const abortController = new AbortController()
    abortControllerRef.current = abortController
    // Set ref immediately to track this request (even if called directly, not just from useEffect)
    prevTripIdRef.current = tripId
    isFetchingRef.current = true

    try {
      setLoading(true)
      setError(null)
      
      // Fetch all trip waypoints (with optional cache control)
      const allWaypoints = await routesApi.getWaypoints({ trip_id: tripId }, options)
      
      // NOTE: Routes are deprecated - we no longer filter by route_id
      // All waypoints returned from getAllWaypointsForTrip should be shown
      // Waypoints are now organized via trip_waypoints junction table, not route_id
      const tripWaypoints = allWaypoints
      
      // Check if request was aborted AFTER processing data but BEFORE setting state
      // This ensures we don't set stale data if a newer request is in flight
      if (abortController.signal.aborted) {
        setLoading(false)
        isFetchingRef.current = false
        return
      }
      
      // Sort by order_index
      tripWaypoints.sort((a, b) => {
        const orderA = a.order_index ?? 999999
        const orderB = b.order_index ?? 999999
        return orderA - orderB
      })
      
      // Double-check abort status and tripId match before setting state
      // This ensures we don't set stale data if tripId changed or request was aborted
      const isStillCurrentRequest = !abortController.signal.aborted && prevTripIdRef.current === tripId
      
      if (isStillCurrentRequest) {
        setWaypoints(tripWaypoints)
      }
    } catch (err) {
      // Ignore abort errors but still clear loading state
      if (err instanceof Error && err.name === 'AbortError') {
        setLoading(false)
        isFetchingRef.current = false
        return
      }
      
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch waypoints'
      setError(errorMessage)
      setWaypoints([])
    } finally {
      // Always clear loading state, even if aborted (handled above, but ensure it's cleared)
      if (!abortController.signal.aborted) {
        setLoading(false)
        isFetchingRef.current = false
      }
    }
  }, [tripId, routesApi])

  /**
   * Add waypoint to trip (without route)
   */
  const addWaypoint = useCallback(async (waypointData: Omit<CreateWaypointDto, 'trip_id'>) => {
    if (!tripId) {
      return null
    }

    // Generate temporary ID for optimistic update
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    
    // Create optimistic waypoint
    const currentMaxOrder = waypoints.length > 0 
      ? Math.max(...waypoints.map(wp => wp.order_index ?? 0))
      : -1
    
    const optimisticWaypoint: WaypointResponse = {
      id: tempId,
      trip_id: tripId,
      route_id: null,
      name: waypointData.name,
      latitude: waypointData.latitude,
      longitude: waypointData.longitude,
      order_index: waypointData.order_index ?? (currentMaxOrder + 1),
      notes: waypointData.notes || '',
      poi_type: waypointData.poi_type,
      source: 'manual',
      created_at: new Date().toISOString(),
      elevation: null
    }

    // Optimistic update
    setWaypoints(prev => {
      const updated = [...prev, optimisticWaypoint]
      return updated.sort((a, b) => {
        const orderA = a.order_index ?? 999999
        const orderB = b.order_index ?? 999999
        return orderA - orderB
      })
    })

    try {
      setError(null)
      
      // Create waypoint with trip_id required, route_id null
      const newWaypoint = await routesApi.createWaypoint({
        trip_id: tripId, // Required
        route_id: null, // Waypoint added to trip, route can be generated later
        ...waypointData
      })

      // Replace optimistic waypoint with real one
      setWaypoints(prev => {
        const withoutTemp = prev.filter(wp => wp.id !== tempId)
        const updated = [...withoutTemp, newWaypoint]
        return updated.sort((a, b) => {
          const orderA = a.order_index ?? 999999
          const orderB = b.order_index ?? 999999
          return orderA - orderB
        })
      })

      return newWaypoint
    } catch (err) {
      // Remove optimistic waypoint on error
      setWaypoints(prev => prev.filter(wp => wp.id !== tempId))
      
      const errorMessage = err instanceof Error ? err.message : 'Failed to add waypoint'
      setError(errorMessage)
      return null
    }
  }, [tripId, waypoints, routesApi])

  /**
   * Update waypoint with optimistic update.
   * Local state is updated immediately; the server is called in the background.
   * State is rolled back if the API call fails.
   */
  const updateWaypoint = useCallback(async (waypointId: string, updates: UpdateWaypointDto) => {
    let previousWaypoints: WaypointResponse[] = []

    // Optimistic update — apply changes instantly before the API responds
    setWaypoints(prev => {
      previousWaypoints = prev
      const updated = prev.map(wp => {
        if (wp.id !== waypointId) return wp
        const merged = { ...wp, ...updates }
        return {
          ...merged,
          start_time: merged.start_time ?? undefined,
          end_time: merged.end_time ?? undefined
        } as WaypointResponse
      })
      return updated.sort((a, b) => (a.order_index ?? 999999) - (b.order_index ?? 999999))
    })

    try {
      setError(null)
      // Use trips API when tripId is set so start_time/end_time (and other junction fields) are persisted to trip_waypoints
      const updatedWaypoint = tripId
        ? await tripsApi.updateWaypoint(tripId, waypointId, updates)
        : await routesApi.updateWaypoint(waypointId, updates, '')

      // Confirm local state with the server response
      setWaypoints(prev => {
        const updated = prev.map(wp => wp.id === waypointId ? updatedWaypoint : wp)
        return updated.sort((a, b) => (a.order_index ?? 999999) - (b.order_index ?? 999999))
      })

      return updatedWaypoint
    } catch (err) {
      // Rollback to previous state
      setWaypoints(previousWaypoints)
      const errorMessage = err instanceof Error ? err.message : 'Failed to update waypoint'
      setError(errorMessage)
      throw err
    }
  }, [tripId, routesApi, tripsApi])

  /**
   * Delete waypoint with optimistic update.
   * Waypoint is removed from local state immediately and restored on failure.
   */
  const deleteWaypoint = useCallback(async (waypointId: string) => {
    let previousWaypoints: WaypointResponse[] = []

    // Optimistic remove
    setWaypoints(prev => {
      previousWaypoints = prev
      return prev.filter(wp => wp.id !== waypointId)
    })

    try {
      setError(null)
      await routesApi.deleteWaypoint(waypointId)
    } catch (err) {
      // Rollback
      setWaypoints(previousWaypoints)
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete waypoint'
      setError(errorMessage)
      throw err
    }
  }, [routesApi])

  /**
   * Reorder waypoints by updating order_index directly on waypoints table.
   * Uses POST /trips/:tripId/waypoints/reorder
   * Applies optimistic ordering immediately; rolls back on failure.
   */
  const reorderWaypoints = useCallback(async (waypointIds: string[]) => {
    if (!tripId) {
      return
    }

    let previousWaypoints: WaypointResponse[] = []

    // Optimistic reorder
    setWaypoints(prev => {
      previousWaypoints = prev
      const waypointMap = new Map(prev.map(wp => [wp.id, wp]))
      const reorderedWaypoints: WaypointResponse[] = []
      waypointIds.forEach((id, index) => {
        const wp = waypointMap.get(id)
        if (wp) reorderedWaypoints.push({ ...wp, order_index: index })
      })
      return reorderedWaypoints
    })

    try {
      setError(null)
      await tripsApi.reorderWaypoints(tripId, waypointIds)
    } catch (err) {
      // Rollback to previous order
      setWaypoints(previousWaypoints)
      const errorMessage = err instanceof Error ? err.message : 'Failed to reorder waypoints'
      setError(errorMessage)
    }
  }, [tripId, tripsApi])

  // Fetch waypoints when tripId changes
  useEffect(() => {
    // Handle null/undefined tripId - clear waypoints
    if (!tripId) {
      if (prevTripIdRef.current !== null) {
        prevTripIdRef.current = null
        setWaypoints([])
        setLoading(false)
      }
      return
    }
    
    // Only fetch if tripId actually changed (including initial mount)
    const hasChanged = prevTripIdRef.current !== tripId
    if (hasChanged) {
      // fetchWaypoints will set prevTripIdRef, so we don't need to set it here
      fetchWaypoints()
    }
    
    // Cleanup: Don't abort requests in cleanup - let them complete naturally
    // Aborting is handled in fetchWaypoints when starting a new request
    // This prevents aborting valid requests during React Strict Mode double renders
    // In Strict Mode: mount -> unmount -> mount, but we want the first request to complete
    return () => {
      // No cleanup needed - requests will complete and check abort status before setting state
      // The abort check in fetchWaypoints ensures we don't set stale data
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]) // Only depend on tripId, not fetchWaypoints to avoid infinite loops

  /**
   * Update a waypoint in local state directly (without API call)
   * Useful for syncing state after an API update from a different endpoint
   * Only works for trip-level waypoints (route_id IS NULL)
   */
  const updateWaypointInState = useCallback((waypointId: string, updatedWaypoint: WaypointResponse) => {
    setWaypoints(prev => {
      const found = prev.find(wp => wp.id === waypointId)
      
      if (!found) {
        // Waypoint not found - this is expected for route-level waypoints
        // They should be updated via route schedule refresh instead
        return prev
      }
      
      // Create a new waypoint object to ensure React detects the change
      const mergedWaypoint: WaypointResponse = {
        ...found,
        ...updatedWaypoint,
        // Ensure time fields are explicitly set from the updated waypoint
        start_time: updatedWaypoint.start_time !== undefined 
          ? (updatedWaypoint.start_time === null ? undefined : updatedWaypoint.start_time)
          : found.start_time,
        end_time: updatedWaypoint.end_time !== undefined 
          ? (updatedWaypoint.end_time === null ? undefined : updatedWaypoint.end_time)
          : found.end_time,
        // Ensure notes field is explicitly set from the updated waypoint
        notes: updatedWaypoint.notes !== undefined
          ? (updatedWaypoint.notes === null ? undefined : updatedWaypoint.notes)
          : found.notes,
      }
      
      const updated = prev.map(wp => wp.id === waypointId ? mergedWaypoint : wp)
      const sorted = updated.sort((a, b) => {
        const orderA = a.order_index ?? 999999
        const orderB = b.order_index ?? 999999
        return orderA - orderB
      })
      
      return sorted
    })
  }, [])

  return {
    waypoints,
    loading,
    error,
    fetchWaypoints,
    addWaypoint,
    updateWaypoint,
    updateWaypointInState,
    deleteWaypoint,
    reorderWaypoints
  }
}
